import axios from 'axios';

// Determine API URL based on environment
// In production (deployed), always use relative URL to ensure frontend talks to same-origin backend
// In development (preview), use environment variable if available
const getApiUrl = () => {
  // Check if we're in production (not on preview domain)
  const isProduction = typeof window !== 'undefined' && 
    !window.location.hostname.includes('preview.emergentagent.com') &&
    !window.location.hostname.includes('localhost');
  
  if (isProduction) {
    // In production, always use relative URL
    return '/api';
  }
  
  // In development/preview, use env var or fallback to relative
  return process.env.REACT_APP_BACKEND_URL 
    ? `${process.env.REACT_APP_BACKEND_URL}/api`
    : '/api';
};

const API_URL = getApiUrl();

// Log API configuration for debugging
console.log('[API Config]', {
  API_URL,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'SSR',
  env_var: process.env.REACT_APP_BACKEND_URL || 'not set'
});

const api = axios.create({ baseURL: API_URL });

// Separate axios instance for the public Careers module (candidate OTP token,
// kept isolated from the staff token so the two never collide).
export const careersApi = axios.create({ baseURL: API_URL });
careersApi.interceptors.request.use((config) => {
  const t = localStorage.getItem('careers_token');
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Request interceptor - log outgoing requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors with detailed logging
api.interceptors.response.use(
  (response) => {
    console.log(`[API Response] ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    // Build detailed error info
    const errorInfo = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      data: error.response?.data,
      code: error.code
    };
    
    console.error('[API Error]', errorInfo);
    
    // Create user-friendly error message
    let userMessage = 'An error occurred';
    
    if (error.code === 'ERR_NETWORK') {
      userMessage = `Network Error: Cannot reach server at ${error.config?.baseURL}. Please check your connection.`;
    } else if (error.code === 'ERR_BAD_REQUEST') {
      userMessage = error.response?.data?.detail || 'Bad request';
    } else if (error.response?.status === 401) {
      userMessage = 'Session expired. Please login again.';
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      userMessage = 'Access denied. You do not have permission.';
    } else if (error.response?.status === 404) {
      userMessage = `API endpoint not found: ${error.config?.url}`;
    } else if (error.response?.status === 500) {
      userMessage = 'Server error. Please try again later.';
    } else if (error.response?.status === 502 || error.response?.status === 503) {
      userMessage = 'Server is temporarily unavailable. Please try again.';
    } else if (error.response?.data?.detail) {
      userMessage = error.response.data.detail;
    }
    
    // Attach user-friendly message to error
    error.userMessage = userMessage;
    error.debugInfo = errorInfo;
    
    return Promise.reject(error);
  }
);

// Export API URL for debugging
export const getDebugInfo = () => ({
  API_URL,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'SSR',
  env_var: process.env.REACT_APP_BACKEND_URL || 'not set'
});

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getCaptcha: () => api.get('/auth/captcha'),
  confirmEmail: (data) => api.post('/auth/confirm-email', data),
  verifyOtp: (data) => api.post('/auth/verify-otp', data),
  resendOtp: (data) => api.post('/auth/resend-otp', data),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => api.put('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  loginAudit: (params) => api.get('/auth/login-audit', { params }),
};

// HR Recruitment (staff/internal)
export const recruitmentAPI = {
  listJobs: () => api.get('/hr/jobs'),
  getJob: (id) => api.get(`/hr/jobs/${id}`),
  createJob: (data) => api.post('/hr/jobs', data),
  updateJob: (id, data) => api.put(`/hr/jobs/${id}`, data),
  deleteJob: (id) => api.delete(`/hr/jobs/${id}`),
  uploadJD: (id, fd) => api.post(`/hr/jobs/${id}/jd`, fd),
  downloadJD: (id) => api.get(`/hr/jobs/${id}/jd`, { responseType: 'blob' }),
  questionTemplate: () => api.get('/hr/jobs-question-template.xlsx', { responseType: 'blob' }),
  importQuestions: (id, fd) => api.post(`/hr/jobs/${id}/import-questions`, fd),
  importAnswerKey: (id, fd) => api.post(`/hr/jobs/${id}/import-answer-key`, fd),
  editQuestions: (id, questions) => api.put(`/hr/jobs/${id}/questions`, { questions }),
  listApplicants: (id) => api.get(`/hr/jobs/${id}/applicants`),
  exportApplicants: (id) => api.get(`/hr/jobs/${id}/applicants/export`, { responseType: 'blob' }),
  downloadResume: (aid) => api.get(`/hr/applications/${aid}/resume`, { responseType: 'blob' }),
  deleteApplicant: (aid) => api.delete(`/hr/applications/${aid}`),
  setApplicantStatus: (aid, status, notify, message) => api.put(`/hr/applications/${aid}/status`, { status, notify, message }),
};

// Public Careers (external candidates)
export const careersAPI = {
  listJobs: () => careersApi.get('/public/jobs'),
  getJob: (id) => careersApi.get(`/public/jobs/${id}`),
  downloadJD: (id) => careersApi.get(`/public/jobs/${id}/jd`, { responseType: 'blob' }),
  register: (data) => careersApi.post('/public/candidate/register', data),
  login: (email) => careersApi.post('/public/candidate/login', { email }),
  verifyOtp: (email, otp) => careersApi.post('/public/candidate/verify-otp', { email, otp }),
  resendOtp: (email) => careersApi.post('/public/candidate/resend-otp', { email }),
  me: () => careersApi.get('/public/candidate/me'),
  uploadResume: (fd) => careersApi.post('/public/candidate/resume', fd),
  applications: () => careersApi.get('/public/candidate/applications'),
  apply: (jid) => careersApi.post(`/public/candidate/jobs/${jid}/apply`),
  getTest: (aid) => careersApi.get(`/public/candidate/applications/${aid}/test`),
  submitTest: (aid, question_ids, answers) => careersApi.post(`/public/candidate/applications/${aid}/test/submit`, { question_ids, answers }),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
};

// Tools
export const toolsAPI = {
  list: (params) => api.get('/tools', { params }),
  get: (id) => api.get(`/tools/${id}`),
  create: (data) => api.post('/tools', data),
  update: (id, data) => api.put(`/tools/${id}`, data),
  delete: (id) => api.delete(`/tools/${id}`),
  eligibleItems: () => api.get('/tools/eligible-items'),
  unlinkedCount: () => api.get('/tools/unlinked-count'),
  backfillItemMaster: () => api.post('/tools/backfill-item-master'),
};

export const hrAPI = {
  // Employees
  listEmployees: () => api.get('/hr/employees'),
  syncEmployees: () => api.post('/hr/employees/sync'),
  createEmployee: (data) => api.post('/hr/employees', data),
  updateEmployee: (id, data) => api.put(`/hr/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/hr/employees/${id}`),
  // Simple masters: kind = skill-groups | functions | positions | qualifications
  listMaster: (kind) => api.get(`/hr/masters/${kind}`),
  createMaster: (kind, data) => api.post(`/hr/masters/${kind}`, data),
  updateMaster: (kind, id, data) => api.put(`/hr/masters/${kind}/${id}`, data),
  deleteMaster: (kind, id) => api.delete(`/hr/masters/${kind}/${id}`),
  reorderQualifications: (ordered_ids) => api.put('/hr/qualifications/reorder', { ordered_ids }),
  seedQualifications: () => api.post('/hr/qualifications/seed'),
  // Skills
  listSkills: (params) => api.get('/hr/skills', { params }),
  createSkill: (data) => api.post('/hr/skills', data),
  updateSkill: (id, data) => api.put(`/hr/skills/${id}`, data),
  deleteSkill: (id) => api.delete(`/hr/skills/${id}`),
  skillsTemplate: () => api.get('/hr/skills/import-template', { responseType: 'blob' }),
  importSkills: (formData) => api.post('/hr/skills/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Skill levels + functional positions
  skillLevels: () => api.get('/hr/skill-levels'),
  listFunctionalPositions: () => api.get('/hr/functional-positions'),
  createFunctionalPosition: (data) => api.post('/hr/functional-positions', data),
  updateFunctionalPosition: (id, data) => api.put(`/hr/functional-positions/${id}`, data),
  deleteFunctionalPosition: (id) => api.delete(`/hr/functional-positions/${id}`),
  employeeEligibility: (id) => api.get(`/hr/employees/${id}/eligibility`),
  positionEligibility: (functional_position_id) => api.get('/hr/reports/position-eligibility', { params: { functional_position_id } }),
  underQualified: () => api.get('/hr/reports/under-qualified'),
  seedDefaults: () => api.post('/hr/seed-defaults'),
  // Phase 2 — employee profiles
  getEmployee: (id) => api.get(`/hr/employees/${id}`),
  updateProfile: (id, data) => api.put(`/hr/employees/${id}/profile`, data),
  setSkillLevels: (id, levels) => api.put(`/hr/employees/${id}/skill-levels`, levels),
  uploadPhoto: (id, formData) => api.post(`/hr/employees/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadAttachment: (id, category, formData) => api.post(`/hr/employees/${id}/attachment?category=${category}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment: (id, attId) => api.delete(`/hr/employees/${id}/attachment/${attId}`),
  skillGaps: (id) => api.get(`/hr/employees/${id}/skill-gaps`),
  reportingTree: () => api.get('/hr/reporting-tree'),
  skillLevelTemplate: () => api.get('/hr/employees/skill-levels/import-template', { responseType: 'blob' }),
  importSkillLevels: (formData) => api.post('/hr/employees/skill-levels/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Phase 3 — training + test
  listTrainings: () => api.get('/hr/trainings'),
  getTraining: (id) => api.get(`/hr/trainings/${id}`),
  createTraining: (data) => api.post('/hr/trainings', data),
  updateTraining: (id, data) => api.put(`/hr/trainings/${id}`, data),
  deleteTraining: (id) => api.delete(`/hr/trainings/${id}`),
  eligibleTrainers: (id) => api.get(`/hr/trainings/${id}/eligible-trainers`),
  uploadMaterial: (id, formData) => api.post(`/hr/trainings/${id}/material`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteMaterial: (id, mid) => api.delete(`/hr/trainings/${id}/material/${mid}`),
  listSessions: (status) => api.get('/hr/training-sessions', { params: status ? { status } : {} }),
  createSession: (data) => api.post('/hr/training-sessions', data),
  updateSession: (id, data) => api.put(`/hr/training-sessions/${id}`, data),
  completeSession: (id, data) => api.post(`/hr/training-sessions/${id}/complete`, data),
  sessionFeedback: (id, data) => api.post(`/hr/training-sessions/${id}/feedback`, data),
  getTest: (id) => api.get(`/hr/training-sessions/${id}/test`),
  submitTest: (id, data) => api.post(`/hr/training-sessions/${id}/test/submit`, data),
  sessionRecord: (id, employeeId) => api.get(`/hr/training-sessions/${id}/record`, { params: employeeId ? { employee_id: employeeId } : {}, responseType: 'blob' }),
  uploadEvalAttachment: (sid, empId, category, formData) => api.post(`/hr/training-sessions/${sid}/attendees/${empId}/attachment?category=${category}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  evalAttachment: (sid, empId, attId) => api.get(`/hr/training-sessions/${sid}/attendees/${empId}/attachment/${attId}`, { responseType: 'blob' }),
  deleteEvalAttachment: (sid, empId, attId) => api.delete(`/hr/training-sessions/${sid}/attendees/${empId}/attachment/${attId}`),
  pendingEvaluations: () => api.get('/hr/pending-evaluations'),
  completedEvaluations: (params) => api.get('/hr/completed-evaluations', { params }),

  // Competency Tests (standalone)
  listTests: () => api.get('/hr/tests'),
  getTest: (id) => api.get(`/hr/tests/${id}`),
  createTest: (data) => api.post('/hr/tests', data),
  updateTest: (id, data) => api.put(`/hr/tests/${id}`, data),
  deleteTest: (id) => api.delete(`/hr/tests/${id}`),
  importTestQuestions: (id, formData) => api.post(`/hr/tests/${id}/import-questions`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importTestAnswerKey: (id, formData) => api.post(`/hr/tests/${id}/import-answer-key`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getAnswerKey: (id) => api.get(`/hr/tests/${id}/answer-key`),
  addCandidate: (id, data) => api.post(`/hr/tests/${id}/candidates`, data),
  listCandidates: (id) => api.get(`/hr/tests/${id}/candidates`),
  deleteCandidate: (id, attId) => api.delete(`/hr/tests/${id}/candidates/${attId}`),
  backfillUnlinkedCandidates: () => api.get('/hr/tests/candidates/unlinked'),
  backfillApplyCandidates: (data) => api.post('/hr/tests/candidates/backfill-apply', data),
  testResults: (id) => api.get(`/hr/tests/${id}/results`),
  takeTest: (id) => api.get(`/hr/tests/${id}/take`),
  submitTest2: (id, data) => api.post(`/hr/tests/${id}/submit`, data),
  downloadTestTemplate: () => api.get('/hr/tests-template.xlsx', { responseType: 'blob' }),
  importTestExcel: (id, formData) => api.post(`/hr/tests/${id}/import-excel`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  publicGetTest: (token) => api.get(`/hr/public/test/${token}`),
  publicSubmitTest: (token, data) => api.post(`/hr/public/test/${token}/submit`, data),
  pendingSkillUpdates: () => api.get('/hr/pending-skill-updates'),
  markSkillUpdated: (sid, empId) => api.post(`/hr/training-sessions/${sid}/attendees/${empId}/skill-updated`),
  extendDue: (sid, empId, data) => api.post(`/hr/training-sessions/${sid}/attendees/${empId}/extend-due`, data),
  runReminders: () => api.post('/hr/reminders/run'),
  runEvalReminders: () => api.post('/hr/reminders/evaluations/run'),

  // Phase 4 — Training Plan
  listPlan: (params) => api.get('/hr/training-plan', { params }),
  planSummary: () => api.get('/hr/training-plan/summary'),
  generatePlanFromGaps: () => api.post('/hr/training-plan/generate-from-gaps'),
  createPlan: (data) => api.post('/hr/training-plan', data),
  updatePlan: (id, data) => api.put(`/hr/training-plan/${id}`, data),
  completePlan: (id, data) => api.post(`/hr/training-plan/${id}/complete`, data),
  reopenPlan: (id) => api.post(`/hr/training-plan/${id}/reopen`),
  deletePlan: (id) => api.delete(`/hr/training-plan/${id}`),
  scheduleSessionFromPlan: (data) => api.post('/hr/training-plan/schedule-session', data),
  runPlanReminders: () => api.post('/hr/training-plan/reminders/run'),

  // Phase 5 — Reports
  planVsActual: () => api.get('/hr/reports/plan-vs-actual'),
  skillAvailability: (params) => api.get('/hr/reports/skill-availability', { params }),
  employeeReport: (id) => api.get(`/hr/reports/employee/${id}`),
  skillMatrixData: (params) => api.get('/hr/reports/skill-matrix-data', { params }),
  criticalSkills: (params) => api.get('/hr/critical-skills', { params }),
  saveCriticalSkillBackup: (skillId, data) => api.put(`/hr/critical-skills/${skillId}`, data),
  runCriticalSkillReviewCheck: () => api.post('/hr/critical-skills/review-check/run'),
  exportSkillMatrix: (params) => api.get('/hr/reports/skill-matrix', { params, responseType: 'blob' }),
  exportPlanVsActual: () => api.get('/hr/reports/plan-vs-actual/export', { responseType: 'blob' }),
  exportEmployeeReport: (id) => api.get(`/hr/reports/employee/${id}/export`, { responseType: 'blob' }),
};

// Calibrations
export const calibrationsAPI = {
  list: (toolId) => api.get(`/calibrations/${toolId}`),
  create: (data) => api.post('/calibrations', data),
};

// Internal Calibration Schedules
export const internalScheduleAPI = {
  list: (toolId) => api.get(`/tools/${toolId}/internal-schedules`),
  create: (toolId, data) => api.post(`/tools/${toolId}/internal-schedules`, data),
  update: (scheduleId, data) => api.put(`/internal-schedules/${scheduleId}`, data),
  remove: (scheduleId) => api.delete(`/internal-schedules/${scheduleId}`),
  listRecords: (scheduleId) => api.get(`/internal-schedules/${scheduleId}/records`),
  addRecord: (scheduleId, data) => api.post(`/internal-schedules/${scheduleId}/records`, data),
};

// Files
export const filesAPI = {
  upload: (formData) => api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  list: (entityType, entityId) => api.get(`/files/list/${entityType}/${entityId}`),
  getUrl: (fileId) => `${API_URL}/files/${fileId}`,
  toggleStatus: (fileId) => api.put(`/files/${fileId}/status`),
};

// Audit Logs
export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
};

// Calibration Report
export const calibrationReportAPI = {
  get: (params) => api.get('/calibration/report', { params }),
  exportMasterList: (params) => api.get('/calibration/master-list/export', { params, responseType: 'blob' }),
};

// Supplier Report
export const supplierReportAPI = {
  get: (params) => api.get('/reports/supplier', { params }),
};

// Users
export const usersAPI = {
  list: () => api.get('/users'),
  updateRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  updateStatus: (id, action) => api.put(`/users/${id}/status?action=${action}`),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) => api.put(`/users/${id}/reset-password`, { new_password: newPassword }),
  updateAppRoles: (id, appRoles) => api.put(`/users/${id}/app-roles`, { app_roles: appRoles }),
  updateContactEmail: (id, contactEmail) => api.put(`/users/${id}/contact-email`, { contact_email: contactEmail }),
  updateDesignation: (id, designation) => api.put(`/users/${id}/designation`, { designation }),
  updateOtpEmail: (id, otpEmail) => api.put(`/users/${id}/otp-email`, { otp_email: otpEmail }),
  // Quality COC Issuer permission
  setCocIssuer: (id, val) => api.put(`/users/${id}/coc-issuer`, { coc_issuer: val }),
  // Signature & Stamp (self-service upload + admin approval)
  mySignatureStamp: () => api.get('/users/me/signature-stamp'),
  uploadMySignatureStamp: (formData) => api.post('/users/me/signature-stamp', formData),
  mySignatureStampImage: () => api.get('/users/me/signature-stamp/image', { responseType: 'blob' }),
  pendingSignatureStamps: () => api.get('/users/signature-stamps/pending'),
  userSignatureStampImage: (id) => api.get(`/users/${id}/signature-stamp/image`, { responseType: 'blob' }),
  signatureStampHistory: (id) => api.get(`/users/${id}/signature-stamp/history`),
  approveSignatureStamp: (id) => api.put(`/users/${id}/signature-stamp/approve`),
  rejectSignatureStamp: (id, reason) => api.put(`/users/${id}/signature-stamp/reject`, { reason }),
};

// ERP Apps
export const erpAppsAPI = {
  list: () => api.get('/erp-apps'),
};

// Notifications
export const notificationsAPI = {
  checkDue: () => api.post('/notifications/check-calibration-due'),
  list: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
};

// Quality Check (challan-based)
export const qualityAPI = {
  dashboard: () => api.get('/quality/dashboard'),
  listPending: (params) => api.get('/quality/pending', { params }),
  listHistory: (params) => api.get('/quality/history', { params }),
  decide: (data) => api.post('/quality/decide', data),
  closeInboundQC: (challanId, data) => api.post(`/quality/close-inbound-qc/${challanId}`, data),
  // Outbound QC (supplier returns from job work)
  listOutboundPending: (params) => api.get('/quality/outbound/pending', { params }),
  listOutboundHistory: (params) => api.get('/quality/outbound/history', { params }),
  receiveAndQC: (data) => api.post('/quality/outbound/receive-and-qc', data),
  // Receipt QC (items with perform_qc_on_receipt flag)
  listReceiptPending: (params) => api.get('/quality/receipt-pending', { params }),
  listReceiptHistory: (params) => api.get('/quality/receipt-history', { params }),
  receiptDecide: (data) => api.post('/quality/receipt-decide', data),
  cleanupJobworkReceiptQC: (params) => api.post('/quality/cleanup-jobwork-receipt-qc', null, { params }),
};

export const qualityPPMAPI = {
  report: (params) => api.get('/quality/ppm/report', { params }),
  getTargets: () => api.get('/quality/ppm/targets'),
  setTargets: (data) => api.put('/quality/ppm/targets', data),
  getEmailSettings: () => api.get('/quality/ppm/email-settings'),
  setEmailSettings: (data) => api.put('/quality/ppm/email-settings', data),
  sendMonthlyEmail: (data) => api.post('/quality/ppm/send-monthly-email', data),
};

export const qualityNcrAPI = {
  reasons: (params) => api.get('/quality/rejection-reasons', { params }),
  addReason: (data) => api.post('/quality/rejection-reasons', data),
  updateReason: (id, data) => api.put(`/quality/rejection-reasons/${id}`, data),
  complaintMeta: () => api.get('/quality/complaints/meta'),
  dispatchSearch: (q, includeUnreconciled) => api.get('/quality/complaints/dispatch-search', { params: { q, include_unreconciled: includeUnreconciled } }),
  customerOptions: () => api.get('/quality/complaints/customer-options'),
  mapComplaintCustomer: (id, data) => api.put(`/quality/complaints/${id}/customer`, data),
  listComplaints: (params) => api.get('/quality/complaints', { params }),
  createComplaint: (data) => api.post('/quality/complaints', data),
  getComplaint: (id) => api.get(`/quality/complaints/${id}`),
  saveComplaintClosure: (id, data) => api.put(`/quality/complaints/${id}/closure`, data),
  refreshTrace: (id) => api.post(`/quality/complaints/${id}/refresh-trace`),
  resolveComplaint: (id) => api.post(`/quality/complaints/${id}/resolve`),
  closeComplaint: (id) => api.post(`/quality/complaints/${id}/close`),
  reopenComplaint: (id) => api.post(`/quality/complaints/${id}/reopen`),
  exportComplaints: (params) => api.get('/quality/complaints/export.xlsx', { params, responseType: 'blob' }),
  listInternal: (params) => api.get('/quality/internal-ncrs', { params }),
  getInternal: (id) => api.get(`/quality/internal-ncrs/${id}`),
  internalByMrb: (caseId) => api.get(`/quality/internal-ncrs/by-mrb/${caseId}`),
  saveInternalClosure: (id, data) => api.put(`/quality/internal-ncrs/${id}/closure`, data),
  setInternalStatus: (id, status) => api.post(`/quality/internal-ncrs/${id}/status`, { status }),
  exportInternal: (params) => api.get('/quality/internal-ncrs/export.xlsx', { params, responseType: 'blob' }),
  save8D: (kind, id, data) => api.put(`/quality/ncr/${kind}/${id}/8d`, data),
  download8D: (kind, id) => api.get(`/quality/ncr/${kind}/${id}/8d.docx`, { responseType: 'blob' }),
  uploadAttachment: (kind, id, file, category) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    return api.post(`/quality/ncr/${kind}/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadAttachment: (kind, id, attId) => api.get(`/quality/ncr/${kind}/${id}/attachments/${attId}`, { responseType: 'blob' }),
  deleteAttachment: (kind, id, attId) => api.delete(`/quality/ncr/${kind}/${id}/attachments/${attId}`),
  legacyImport: (kind, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/quality/legacy-import/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  replyDraft: (id) => api.get(`/quality/complaints/${id}/reply-draft`),
  sendReply: (id, data) => api.post(`/quality/complaints/${id}/send-reply`, data),
  pareto: (params) => api.get('/quality/ppm/pareto', { params }),
  classifyGroups: (kind) => api.get(`/quality/classify/${kind}`),
  classifyApply: (kind, data) => api.post(`/quality/classify/${kind}`, data),
  scorecard: (params) => api.get('/quality/scorecard', { params }),
};

// Material Review Board (MRB)
export const mrbAPI = {
  dashboard: () => api.get('/mrb/dashboard'),
  alerts: () => api.get('/mrb/alerts'),
  listCases: (params) => api.get('/mrb/cases', { params }),
  getCase: (id) => api.get(`/mrb/cases/${id}`),
  updateCustomerWO: (id, data) => api.patch(`/mrb/cases/${id}/customer-wo`, data),
  setDisposition: (id, data) => api.post(`/mrb/cases/${id}/disposition`, data),
  approveReaccept: (id, data) => api.post(`/mrb/cases/${id}/approve-reaccept`, data),
  uploadCustomerAcceptance: (id, formData) => api.post(`/mrb/cases/${id}/upload-customer-acceptance`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadCustomerAcceptance: (id) => api.get(`/mrb/cases/${id}/customer-acceptance`, { responseType: 'blob' }),
  sendToCustomer: (id, data) => api.post(`/mrb/cases/${id}/send-to-customer`, data),
  recordCustomerDisposition: (id, formData) => api.post(`/mrb/cases/${id}/customer-disposition`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadCustomerDispositionFile: (id) => api.get(`/mrb/cases/${id}/customer-disposition-file`, { responseType: 'blob' }),
  // Notes
  addNote: (id, data) => api.post(`/mrb/cases/${id}/notes`, data),
  deleteNote: (id, noteId) => api.delete(`/mrb/cases/${id}/notes/${noteId}`),
  // Attachments
  stagePhoto: (formData) => api.post('/mrb/staged-photos', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadAttachment: (id, formData) => api.post(`/mrb/cases/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadAttachment: (id, fileId) => api.get(`/mrb/cases/${id}/attachments/${fileId}`, { responseType: 'blob' }),
  previewAttachment: (id, fileId) => api.get(`/mrb/cases/${id}/attachments/${fileId}/preview`, { responseType: 'blob' }),
  deleteAttachment: (id, fileId) => api.delete(`/mrb/cases/${id}/attachments/${fileId}`),
  // Admin repair
  repairSearchChallans: (search) => api.get('/mrb/admin/repair/inbound-challans', { params: { search } }),
  repairChallanOkQty: (data) => api.post('/mrb/admin/repair-challan-ok-qty', data),
};

// Dashboard Analytics
export const analyticsAPI = {
  dashboard: () => api.get('/analytics/dashboard'),
};

// Suppliers
export const suppliersAPI = {
  list: (params) => api.get('/suppliers', { params }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  approvedList: (params) => api.get('/suppliers/approved-list', { params }),
  dashboard: () => api.get(`/suppliers/dashboard?_t=${Date.now()}`),
  pendingReevaluation: () => api.get('/suppliers/pending-reevaluation'),
  startReevaluation: (id) => api.post(`/suppliers/${id}/start-reevaluation`),
  submitEvaluation: (id, data) => api.post(`/suppliers/${id}/submit-evaluation`, data),
  performance: (id, params = {}) => api.get(`/suppliers/${id}/performance`, { params }),
  performanceReport: (data) => api.post('/suppliers/performance-report', data),
  reevaluationHistory: (id) => api.get(`/suppliers/${id}/reevaluation-history`),
  checkReevaluationDue: () => api.post('/suppliers/check-reevaluation-due'),
  importExcel: (formData) => api.post('/suppliers/import-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importTemplate: () => api.get('/suppliers/import-template'),
  approveNonAs: (id) => api.put(`/suppliers/${id}/approve-non-as`),
  rejectNonAs: (id) => api.put(`/suppliers/${id}/reject-non-as`),
  exportAslReport: () => api.get('/suppliers/asl-report/export', { responseType: 'blob' }),
};

// Scopes of Approval
export const scopesAPI = {
  list: () => api.get('/scopes-of-approval'),
  create: (data) => api.post('/scopes-of-approval', data),
  delete: (id) => api.delete(`/scopes-of-approval/${id}`),
};

// Scheduler
export const schedulerAPI = {
  status: () => api.get('/scheduler/status'),
};

// Inventory
export const inventoryAPI = {
  dashboard: () => api.get('/inventory/dashboard'),
  // Admin — production receipt / trace_lot cleanup
  listWoReceipts: (woId) => api.get(`/inventory/admin/wo/${woId}/receipts`),
  cancelProductionReceipt: (txnId, data) => api.post(`/inventory/admin/cancel-production-receipt/${txnId}`, data),
  listStaleFgTraceLots: (params) => api.get('/inventory/admin/stale-fg-trace-lots', { params }),
  deleteTraceLots: (data) => api.post('/inventory/admin/delete-trace-lots', data),
  inventoryTraceDrift: (params) => api.get('/inventory/admin/inventory-trace-drift', { params }),
  reconcileInventoryTraceDrift: (data) => api.post('/inventory/admin/reconcile-inventory-trace-drift', data),
  manifestStockDebug: (params) => api.get('/inventory/admin/manifest-stock-debug', { params }),
  traceLotSearch: (params) => api.get('/inventory/admin/trace-lot-search', { params }),
  editTraceLot: (data) => api.post('/inventory/admin/edit-trace-lot', data),
  autoFixStaleFgLots: (data) => api.post('/inventory/admin/auto-fix-stale-fg-lots', data),
  backfillReceiptQc: (data) => api.post('/inventory/admin/backfill-receipt-qc', data),
  reconcileSubcontractChallan: (data) => api.post('/inventory/admin/reconcile-subcontract-challan', data),
  // Categories
  listCategories: () => api.get('/inventory/categories'),
  createCategory: (data) => api.post('/inventory/categories', data),
  updateCategory: (id, data) => api.put(`/inventory/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/inventory/categories/${id}`),
  // Pending Approvals (Admin)
  getPendingApprovals: () => api.get('/inventory/pending-approvals'),
  // Items
  listItems: (params) => api.get('/inventory/items', { params }),
  getItem: (id) => api.get(`/inventory/items/${id}`),
  createItem: (data) => api.post('/inventory/items', data),
  updateItem: (id, data) => api.put(`/inventory/items/${id}`, data),
  deleteItem: (id) => api.delete(`/inventory/items/${id}`),
  getItemChangelog: (id) => api.get(`/inventory/items/${id}/changelog`),
  bulkCopyItems: (itemIds) => api.post('/inventory/items/bulk-copy', { item_ids: itemIds }),
  suspendItem: (id) => api.patch(`/inventory/items/${id}/suspend`),
  approveItem: (id) => api.patch(`/inventory/items/${id}/approve`),
  rejectItem: (id, reason) => api.patch(`/inventory/items/${id}/reject`, { reason }),
  // Godowns
  listGodowns: () => api.get('/inventory/godowns'),
  createGodown: (data) => api.post('/inventory/godowns', data),
  updateGodown: (id, data) => api.put(`/inventory/godowns/${id}`, data),
  deleteGodown: (id) => api.delete(`/inventory/godowns/${id}`),
  // Job Work Items (for inbound subcontracting)
  listJobWorkItems: () => api.get('/inventory/items/job-work/list'),
  // Transactions
  listTransactions: (params) => api.get('/inventory/transactions', { params }),
  createTransaction: (data) => api.post('/inventory/transactions', data),
  getPendingWorkOrders: (itemId) => api.get('/inventory/pending-work-orders', { params: itemId ? { item_id: itemId } : {} }),
  // Calibration ↔ Subcontract Integration
  sendForCalibration: (params) => api.post('/inventory/calibration/send-for-calibration', null, { params }),
  receiveFromCalibration: (params) => api.post('/inventory/calibration/receive-from-calibration', null, { params }),
  // Stock
  getStock: (params) => api.get('/inventory/stock', { params }),
  // Stock Transfer (Admin)
  transferStock: (data) => api.post('/inventory/stock/transfer', data),
  listTransfers: (params) => api.get('/inventory/stock/transfers', { params }),
  // Stock Reconciliation (Admin)
  reconcileStock: (data) => api.post('/inventory/stock/reconcile', data),
  listReconciliations: (params) => api.get('/inventory/stock/reconciliations', { params }),
  // Routes
  listRoutes: (params) => api.get('/inventory/routes', { params }),
  getRoute: (id) => api.get(`/inventory/routes/${id}`),
  createRoute: (data) => api.post('/inventory/routes', data),
  updateRoute: (id, data) => api.put(`/inventory/routes/${id}`, data),
  deleteRoute: (id) => api.delete(`/inventory/routes/${id}`),
  suspendRoute: (id) => api.put(`/inventory/routes/${id}/suspend`),
  approveRoute: (id) => api.patch(`/inventory/routes/${id}/approve`),
  rejectRoute: (id, reason) => api.patch(`/inventory/routes/${id}/reject`, { reason }),
  // Work Centers
  listWorkCenters: () => api.get('/inventory/work-centers'),
  createWorkCenter: (data) => api.post('/inventory/work-centers', data),
  updateWorkCenter: (id, data) => api.put(`/inventory/work-centers/${id}`, data),
  deleteWorkCenter: (id) => api.delete(`/inventory/work-centers/${id}`),
  // Work Orders
  listWorkOrders: (params) => api.get('/inventory/work-orders', { params }),
  getWorkOrder: (id) => api.get(`/inventory/work-orders/${id}`),
  createWorkOrder: (data) => api.post('/inventory/work-orders', data),
  updateWorkOrder: (id, data) => api.put(`/inventory/work-orders/${id}`, data),
  deleteWorkOrder: (id) => api.delete(`/inventory/work-orders/${id}`),
  forceCloseWorkOrder: (id, data) => api.post(`/inventory/work-orders/${id}/force-close`, data),
  reopenWorkOrder: (id) => api.post(`/inventory/work-orders/${id}/reopen`),
  productionReceipt: (id, data) => api.post(`/inventory/work-orders/${id}/production-receipt`, data),
  getTraceLotsForWO: (id) => api.get(`/inventory/work-orders/${id}/trace-lots`),
  getTraceLots: (params) => api.get('/inventory/trace-lots', { params }),
  getTraceLifecycle: (traceId) => api.get(`/inventory/trace-lots/${encodeURIComponent(traceId)}/lifecycle`),
  traceSearch: (params) => api.get('/inventory/trace-search', { params }),
  traceLifecycleDetail: (id) => api.get(`/inventory/trace-search/${id}/lifecycle`),
  getChallanLineItemsForRoute: (routeId) => api.get(`/inventory/work-orders/challan-line-items-for-route/${routeId}`),
  getBomStoreLots: (routeId) => api.get(`/inventory/routes/${routeId}/bom-store-lots`),
  backfillChallanLineItems: () => api.post('/inventory/work-orders/backfill-challan-line-items'),
  // Scrap disposal
  getScrapItems: () => api.get('/inventory/scrap/items'),
  disposeScrap: (data) => api.post('/inventory/scrap/dispose', data),
  // Users with inventory access
  getInventoryUsers: () => api.get('/inventory/users'),
  // Item → Program linking
  getItemLinkedPrograms: (itemId) => api.get(`/inventory/items/${itemId}/linked-programs`),
  // Misc Receipts (Non-Inventory Spend Tracking)
  createMiscReceipt: (data) => api.post('/inventory/misc-receipts', data),
  listMiscReceipts: (params) => api.get('/inventory/misc-receipts', { params }),
  linkMiscReceiptProgram: (id, data) => api.put(`/inventory/misc-receipts/${id}/link-program`, data),
  miscReceiptSpendSummary: (params) => api.get('/inventory/misc-receipts/spend-summary', { params }),
  linkItemToProgram: (itemId, data) => api.post(`/inventory/items/${itemId}/link-to-program`, data),
  // FIFO stock lots
  getStockLots: (itemId, params) => api.get(`/inventory/items/${itemId}/stock-lots`, { params }),
  // Qty Discrepancy (Short/Excess)
  reportQtyDiscrepancy: (data) => api.post('/inventory/qty-discrepancy', data),
  listQtyDiscrepancies: (params) => api.get('/inventory/qty-discrepancies', { params }),
  sendDiscrepancyEmail: (discId, data) => api.post(`/inventory/qty-discrepancies/${discId}/send-email`, data),
  // Goods-receipt correction (admin only)
  reverseReceiveTxn: (txnId) => api.post(`/inventory/transactions/${txnId}/reverse-receipt`),
  reversePurchaseReceipt: (receiptId) => api.post(`/inventory/purchase-receipts/${receiptId}/reverse`),
  editReceiptQty: (receiptId, data) => api.put(`/inventory/purchase-receipts/${receiptId}/quantity`, data),
  // Stock Aging Report
  getStockAging: (params) => api.get('/inventory/stock-aging', { params }),
  // Trace Consumption Report
  traceConsumptionReport: (q) => api.get('/inventory/trace-consumption-report', { params: { q } }),
};

// Item Files
export const itemFilesAPI = {
  list: (itemId) => api.get(`/inventory/items/${itemId}/files`),
  upload: (itemId, formData) => api.post(`/inventory/items/${itemId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  download: (itemId, fileId) => api.get(`/inventory/items/${itemId}/files/${fileId}/download`, { responseType: 'blob' }),
  delete: (itemId, fileId) => api.delete(`/inventory/items/${itemId}/files/${fileId}`),
};

// Low Stock Report
export const lowStockAPI = {
  getReport: (itemType) => api.get('/inventory/low-stock-report', { params: itemType ? { item_type: itemType } : {} }),
  getReportByMakeOrBuy: (makeOrBuy) => api.get('/inventory/low-stock-report', { params: makeOrBuy ? { make_or_buy: makeOrBuy } : {} }),
  getItemTypes: () => api.get('/inventory/low-stock-report/item-types'),
  downloadPDF: (supplierId, itemType, makeOrBuy) => api.get('/inventory/low-stock-report/pdf', {
    params: { 
      ...(supplierId ? { supplier_id: supplierId } : {}), 
      ...(itemType ? { item_type: itemType } : {}),
      ...(makeOrBuy ? { make_or_buy: makeOrBuy } : {}),
    },
    responseType: 'blob',
  }),
  emailSupplier: (supplierId) => api.post(`/inventory/low-stock-report/email/${supplierId}`),
};

// Purchase Module
export const purchaseAPI = {
  dashboard: () => api.get('/purchase/dashboard'),
  // Indent
  addToIndent: (data) => api.post('/purchase/indent/add', data),
  addBulkToIndent: (items) => api.post('/purchase/indent/add-bulk', items),
  listIndent: (params) => api.get('/purchase/indent', { params }),
  removeFromIndent: (id) => api.delete(`/purchase/indent/${id}`),
  updateIndentItem: (id, data) => api.put(`/purchase/indent/${id}`, data),
  // Auto-Indent
  autoIndentPreview: () => api.get('/purchase/auto-indent/preview'),
  autoIndentRun: () => api.post('/purchase/auto-indent/run'),
  // RFQ
  sendRFQ: (data) => api.post('/purchase/rfq/send', data),
  sendRFQWithAttachments: (formData) => api.post('/purchase/rfq/send-with-attachments', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Purchase Requests
  createPurchaseRequest: (data) => api.post('/purchase/purchase-request', data),
  listPurchaseRequests: (params) => api.get('/purchase/purchase-requests', { params }),
  getPurchaseRequest: (id) => api.get(`/purchase/purchase-requests/${id}`),
  approvePurchaseRequest: (id, data) => api.put(`/purchase/purchase-requests/${id}/approve`, data),
  approvePurchaseRequestWithQuote: (id, formData) => api.post(`/purchase/purchase-requests/${id}/approve-with-quote`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // PO Generation & Sending
  generatePO: (prId, data) => api.put(`/purchase/purchase-requests/${prId}/generate-po`, data),
  updatePONumber: (prId, data) => api.put(`/purchase/purchase-requests/${prId}/po-number`, data),
  extractPONumber: (formData) => api.post('/purchase/extract-po-number', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadPOFile: (prId, formData) => api.post(`/purchase/purchase-requests/${prId}/upload-po-file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadPOFile: (prId, fileId) => api.get(`/purchase/purchase-requests/${prId}/po-file/${fileId}`, { responseType: 'blob' }),
  uploadQuoteFile: (prId, formData) => api.post(`/purchase/purchase-requests/${prId}/upload-quote`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadQuoteFile: (prId, fileId) => api.get(`/purchase/purchase-requests/${prId}/quote-file/${fileId}`, { responseType: 'blob' }),
  sendPO: (prId, data) => api.post(`/purchase/purchase-requests/${prId}/send-po`, data),
  cancelPO: (prId, data) => api.post(`/purchase/purchase-requests/${prId}/cancel`, data),
  shortClosePO: (prId, data) => api.post(`/purchase/purchase-requests/${prId}/short-close`, data),
  // Open POs for receipt
  listOpenPOs: () => api.get('/purchase/open-pos'),
  getPOReceiptStatus: (prId) => api.get(`/purchase/${prId}/receipt-status`),
  getItemsProcurementStatus: (itemIds) => api.post('/purchase/items-procurement-status', { item_ids: itemIds }),
  itemsPriceHistory: (itemIds) => api.post('/purchase/items-price-history', { item_ids: itemIds }),
  receiveAgainstPO: (prId, items) => api.post(`/purchase/receive-against-po/${prId}`, items),
  // Goods-receipt correction (admin only) — same endpoints as inventoryAPI
  reversePurchaseReceipt: (receiptId) => api.post(`/inventory/purchase-receipts/${receiptId}/reverse`),
  editReceiptQty: (receiptId, data) => api.put(`/inventory/purchase-receipts/${receiptId}/quantity`, data),
  // Lookups for direct PR
  getSuppliers: () => api.get('/purchase/suppliers'),
  getItems: (q) => api.get('/purchase/items', { params: q ? { q } : {} }),
  // Analytics
  getSpendAnalytics: (params) => api.get('/purchase/analytics/spend', { params }),
};

// Subcontract (Multi-item Challans)
export const subcontractAPI = {
  dashboard: () => api.get('/subcontract/dashboard'),
  getDashboardKPIs: () => api.get('/subcontract/dashboard'),
  listChallans: (params) => api.get('/subcontract/challans', { params }),
  getChallan: (id) => api.get(`/subcontract/challans/${id}`),
  createChallan: (data) => api.post('/subcontract/challans', data),
  downloadImportTemplate: () => api.get('/subcontract/import/template', { responseType: 'blob' }),
  importChallanItems: (formData) => api.post('/subcontract/import/items', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  notifyLowStockCustomers: (params) => api.post('/subcontract/notify-low-stock-customers', null, { params }),
  sendChallan: (id) => api.put(`/subcontract/challans/${id}/send`),
  sendChallanWithEmail: (id, data) => api.put(`/subcontract/challans/${id}/send-with-email`, data),
  getSupplierEmail: (id) => api.get(`/subcontract/challans/${id}/supplier-email`),
  deleteChallan: (id) => api.delete(`/subcontract/challans/${id}`),
  receiveChallan: (id, data) => api.post(`/subcontract/challans/${id}/receive`, data),
  downloadChallanPDF: (id) => api.get(`/subcontract/challans/${id}/pdf`, { responseType: 'blob' }),
  getRecalledInstruments: (supplierId) => api.get(`/subcontract/recalled-instruments/${supplierId}`),
  // PO Linking
  linkPO: (challanId, data) => api.put(`/subcontract/challans/${challanId}/link-po`, data),
  getPOsForLinking: (params) => api.get('/subcontract/purchase-orders/for-linking', { params }),
  // Inbound job work integration
  getInboundDispatchStatus: (challanId) => api.get(`/subcontract/inbound/${challanId}/dispatch-status`),
  getOutboundReceiptStatus: (challanId) => api.get(`/subcontract/outbound/${challanId}/receipt-status`),
  closeInboundChallan: (challanId, data) => api.post(`/subcontract/inbound/${challanId}/close`, data || {}),
  getOpenInboundChallans: (customerId) => api.get(`/subcontract/inbound/open-for-customer/${customerId}`),
  getExpectedOutputItems: (challanId) => api.get(`/subcontract/inbound/${challanId}/expected-output-items`),
  getJobWorkDashboard: (params) => api.get('/subcontract/job-work-dashboard', { params }),
  getCustomerPOs: (customerId) => api.get(`/subcontract/customer-pos/${customerId}`),
  // Service Entry & Closure
  createServiceEntry: (challanId, data) => api.post(`/subcontract/challans/${challanId}/service-entry`, data),
  listServiceEntries: (challanId) => api.get(`/subcontract/challans/${challanId}/service-entries`),
  closeOutboundChallan: (challanId, data) => api.put(`/subcontract/challans/${challanId}/close`, data || {}),
  reopenChallan: (challanId, data) => api.post(`/subcontract/challans/${challanId}/reopen`, data),
  // Admin: Backfill Customer DC/PO on inbound challans
  listMissingCustomerRefs: (params) => api.get('/subcontract/admin/inbound-missing-customer-refs', { params }),
  updateChallanCustomerRefs: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/customer-refs`, data),
  // Admin: correct inbound challan entry (trace id / received-before-QC quantity)
  updateTraceability: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/update-traceability`, data),
  correctReceivedQty: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/correct-received-qty`, data),
  correctItemDueDate: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/correct-item-due-date`, data),
  correctChallanItem: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/correct-item`, data),
  // Inbound challans available for closure
  listInboundReadyForClosure: (params) => api.get('/subcontract/inbound-ready-for-closure', { params }),
  // Customer material traceability report (WO / DC / PO)
  traceabilityReport: (params) => api.get('/subcontract/traceability-report', { params }),
  exportTraceabilityReport: (params) => api.get('/subcontract/traceability-report/export', { params, responseType: 'blob' }),
};

// Contract Management
export const contractsAPI = {
  dashboard: () => api.get('/contracts/dashboard'),
  // Customers
  listCustomers: (params) => api.get('/contracts/customers', { params }),
  activeCustomers: () => api.get('/contracts/customers/active-list'),
  getCustomer: (id) => api.get(`/contracts/customers/${id}`),
  createCustomer: (data) => api.post('/contracts/customers', data),
  updateCustomer: (id, data) => api.put(`/contracts/customers/${id}`, data),
  suspendCustomer: (id) => api.put(`/contracts/customers/${id}/suspend`),
  deleteCustomer: (id) => api.delete(`/contracts/customers/${id}`),
  // Purchase Orders
  listPurchaseOrders: (params) => api.get('/contracts/purchase-orders', { params }),
  listOpenPOs: (params) => api.get('/contracts/purchase-orders-open', { params }),
  getPurchaseOrder: (id) => api.get(`/contracts/purchase-orders/${id}`),
  createPurchaseOrder: (data) => api.post('/contracts/purchase-orders', data),
  updatePurchaseOrder: (id, data) => api.put(`/contracts/purchase-orders/${id}`, data),
  closePurchaseOrder: (id, data) => api.post(`/contracts/purchase-orders/${id}/close`, data),
  reopenPurchaseOrder: (id, data) => api.post(`/contracts/purchase-orders/${id}/reopen`, data),
  // PO Files
  uploadPOFile: (poId, formData) => api.post(`/contracts/purchase-orders/${poId}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadPOFile: (poId, fileId) => api.get(`/contracts/purchase-orders/${poId}/files/${fileId}/download`, { responseType: 'blob' }),
  deletePOFile: (poId, fileId) => api.delete(`/contracts/purchase-orders/${poId}/files/${fileId}`),
  // Contract Review
  sendAcknowledgment: (poId, body) => api.post(`/contracts/purchase-orders/${poId}/send-acknowledgment`, body),
  resolveEmails: (poId) => api.get(`/contracts/purchase-orders/${poId}/resolve-emails`),
  uploadReviewDocument: (poId, formData) => api.post(`/contracts/purchase-orders/${poId}/review/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  closeReview: (poId) => api.put(`/contracts/purchase-orders/${poId}/review/close`),
  reopenReview: (poId) => api.put(`/contracts/purchase-orders/${poId}/review/reopen`),
  pendingReviews: () => api.get('/contracts/reviews/pending'),
  // On-Time Delivery Report
  otdReport: (params) => api.get('/contracts/otd-report', { params }),
  otdReportExport: (params) => api.get('/contracts/otd-report/export', { params, responseType: 'blob' }),
};

// ======================== DISPATCH API ========================
export const dispatchAPI = {
  dashboard: () => api.get('/dispatch/dashboard'),
  listChallans: (params) => api.get('/dispatch/challans', { params }),
  getChallan: (id) => api.get(`/dispatch/challans/${id}`),
  createChallan: (data) => api.post('/dispatch/challans', data),
  reconcileChallan: (id, data) => api.post(`/dispatch/challans/${id}/reconcile`, data),
  downloadPDF: (id) => api.get(`/dispatch/challans/${id}/pdf`, { responseType: 'blob' }),
  getPendingScrap: (params) => api.get('/dispatch/scrap-pending', { params }),
  // Invoice attachment
  uploadInvoice: (challanId, formData) => api.post(`/dispatch/challans/${challanId}/upload-invoice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadInvoice: (challanId) => api.get(`/dispatch/challans/${challanId}/invoice`, { responseType: 'blob' }),
  downloadCOC: (challanId, { itemIndex = 0, rmLotOverride, customerPoOverride, ncrRefOverride, shelfLifeMfgOverride, qtyDispatchedOverride, qtyAcceptedOverride, attach = false, fmt = 'pdf' } = {}) => {
    const params = { item_index: itemIndex, fmt };
    if (rmLotOverride) params.rm_lot_override = rmLotOverride;
    if (customerPoOverride) params.customer_po_override = customerPoOverride;
    if (ncrRefOverride) params.ncr_ref_override = ncrRefOverride;
    if (shelfLifeMfgOverride) params.shelf_life_mfg_override = shelfLifeMfgOverride;
    if (qtyDispatchedOverride !== undefined && qtyDispatchedOverride !== null && qtyDispatchedOverride !== '') params.qty_dispatched_override = qtyDispatchedOverride;
    if (qtyAcceptedOverride !== undefined && qtyAcceptedOverride !== null && qtyAcceptedOverride !== '') params.qty_accepted_override = qtyAcceptedOverride;
    if (attach) params.attach = true;
    return api.get(`/dispatch/challans/${challanId}/coc`, { params, responseType: 'blob' });
  },
  listCocLines: (challanId) => api.get(`/dispatch/challans/${challanId}/coc-lines`),
  downloadCOCBundle: (challanId, { fmt = 'pdf', itemIndices, attach = false } = {}) => {
    const params = { fmt };
    if (Array.isArray(itemIndices) && itemIndices.length) params.item_indices = itemIndices.join(',');
    if (attach) params.attach = true;
    return api.get(`/dispatch/challans/${challanId}/coc-bundle`, { params, responseType: 'blob' });
  },
  listCocAttachments: (challanId) => api.get(`/dispatch/challans/${challanId}/coc-attachments`),
  downloadCocAttachment: (challanId, attachId) => api.get(`/dispatch/challans/${challanId}/coc-attachments/${attachId}/download`, { responseType: 'blob' }),
  // Manifests
  listManifests: (params) => api.get('/dispatch/manifests', { params }),
  getManifest: (id) => api.get(`/dispatch/manifests/${id}`),
  createManifest: (data) => api.post('/dispatch/manifests', data),
  previewManifest: (data) => api.post('/dispatch/manifests/preview', data),
  downloadManifestPDF: (id) => api.get(`/dispatch/manifests/${id}/pdf`, { responseType: 'blob' }),
  emailManifest: (id, data) => api.post(`/dispatch/manifests/${id}/email`, data),
  createChallansFromManifest: (id) => api.post(`/dispatch/manifests/${id}/create-challans`),
  deleteManifest: (id) => api.delete(`/dispatch/manifests/${id}`),
  // Scrap Tracking
  scrapDashboard: () => api.get('/dispatch/scrap/dashboard'),
  listPendingScrap: (params) => api.get('/dispatch/scrap/pending', { params }),
  recordScrap: (data) => api.post('/dispatch/scrap/record', data),
  // Customer dispatch reports
  getCustomerDispatchedItems: (params) => api.get('/dispatch/reports/customer-dispatched-items', { params }),
  exportDispatchPlan: (data) => api.post('/dispatch/reports/dispatch-plan', data, { responseType: 'blob' }),
  getCustomerRmJwItems: (params) => api.get('/dispatch/reports/customer-rm-jw-items', { params }),
  exportRmJwStock: (data) => api.post('/dispatch/reports/rm-jw-stock', data, { responseType: 'blob' }),
  exportDispatchHistory: (data) => api.post('/dispatch/reports/customer-dispatch-history', data, { responseType: 'blob' }),
  dispatchDetails: (data) => api.post('/dispatch/reports/dispatch-details', data),
  exportDispatchDetails: (data) => api.post('/dispatch/reports/dispatch-details/export', data, { responseType: 'blob' }),
  updateScrapEntry: (id, data) => api.put(`/dispatch/scrap/${id}`, null, { params: data }),
  listScrapReturns: (params) => api.get('/dispatch/scrap/returns', { params }),
  createScrapReturn: (data) => api.post('/dispatch/scrap/returns', data),
  downloadScrapReturnPDF: (id) => api.get(`/dispatch/scrap/returns/${id}/pdf`, { responseType: 'blob' }),
  // Post-Dispatch Customer Email
  emailToCustomer: (data) => api.post('/dispatch/email-to-customer', data),
  previewCustomerEmail: (data) => api.post('/dispatch/email-to-customer', { ...data, preview_only: true }),
  updateInvoiceNumbers: (challanId, invoiceNumbers) => api.put(`/dispatch/challans/${challanId}/invoice-numbers`, { invoice_numbers: invoiceNumbers }),
  oneClickDispatch: (manifestId, data) => api.post(`/dispatch/manifests/${manifestId}/one-click-dispatch`, data),
  getManifestRecipients: (manifestId) => api.get(`/dispatch/manifests/${manifestId}/recipient-emails`),
  // Supplier Return
  supplierReturnPreview: (data) => api.post('/dispatch/supplier-return/preview', data),
  supplierReturnCreateManifest: (data) => api.post('/dispatch/supplier-return/create-manifest', data),
  supplierReturnCreateChallans: (manifestId) => api.post(`/dispatch/supplier-return/manifests/${manifestId}/create-challans`),
  // Legacy fix
  backfillCustomerInfo: () => api.post('/dispatch/challans/backfill-customer-info'),
  // Admin: audit + correct customer DC# / PO# on existing manifests / DCs
  customerRefAudit: (params) => api.get('/dispatch/admin/customer-ref-audit', { params }),
  customerRefCorrect: (data) => api.post('/dispatch/admin/customer-ref-correct', data),
  auditPrematurelyClosedPOs: (params) => api.get('/dispatch/admin/audit-prematurely-closed-pos', { params }),
  reopenPrematurelyClosedPOs: (data) => api.post('/dispatch/admin/reopen-prematurely-closed-pos', data),
  // Admin: cancel a manifest (and its linked DCs), restoring stock + trace_lots
  listCancellableManifests: (params) => api.get('/dispatch/admin/cancellable-manifests', { params }),
  cancelManifest: (manifestId, data) => api.post(`/dispatch/admin/cancel-manifest/${manifestId}`, data),
  // Admin: cancel a single delivery challan, restoring stock + trace_lots
  cancelChallan: (challanId, data) => api.post(`/dispatch/challans/${challanId}/cancel`, data),
  // Dispatch Tag
  downloadDispatchTag: (manifestId) => api.get(`/dispatch/manifests/${manifestId}/dispatch-tag`, { responseType: 'blob' }),
};

// ======================== PROGRAMS API ========================
export const programsAPI = {
  list: (params) => api.get('/programs', { params }),
  get: (id) => api.get(`/programs/${id}`),
  create: (data) => api.post('/programs', data),
  update: (id, data) => api.put(`/programs/${id}`, data),
  delete: (id) => api.delete(`/programs/${id}`),
  analytics: () => api.get('/programs/analytics'),
  financials: (id) => api.get(`/programs/${id}/financials`),
  // PO linking
  linkPO: (id, formData) => api.post(`/programs/${id}/link-po`, formData),
  unlinkPO: (id, poId) => api.delete(`/programs/${id}/unlink-po/${poId}`),
  // Items
  addItem: (id, data) => api.post(`/programs/${id}/items`, data),
  updateItem: (id, itemId, data) => api.put(`/programs/${id}/items/${itemId}`, data),
  removeItem: (id, itemId) => api.delete(`/programs/${id}/items/${itemId}`),
  // Tasks
  createTask: (id, data) => api.post(`/programs/${id}/tasks`, data),
  updateTask: (id, taskId, data) => api.put(`/programs/${id}/tasks/${taskId}`, data),
  deleteTask: (id, taskId) => api.delete(`/programs/${id}/tasks/${taskId}`),
  // Cross-program tasks
  myTasks: (params) => api.get('/programs/my-tasks/list', { params }),
  taskDashboard: () => api.get('/programs/task-dashboard/analytics'),
  // Files
  uploadFile: (id, formData) => api.post(`/programs/${id}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadFile: (id, fileId) => api.get(`/programs/${id}/files/${fileId}/download`, { responseType: 'blob' }),
  deleteFile: (id, fileId) => api.delete(`/programs/${id}/files/${fileId}`),
  // Price sync (dynamic budget ↔ actuals reconciliation)
  syncPrices: (id, thresholdPct = 0) => api.post(`/programs/${id}/sync-prices`, null, { params: { threshold_pct: thresholdPct } }),
  // Admin: item linkage conflicts (items linked to > 1 program)
  listLinkageConflicts: () => api.get('/programs/admin/item-linkage-conflicts'),
  resolveLinkageConflict: (data) => api.post('/programs/admin/item-linkage-conflicts/resolve', data),
};

// Help API
export const helpAPI = {
  getCategories: () => api.get('/help/categories'),
  getCategoryArticles: (categoryId) => api.get(`/help/categories/${categoryId}`),
  getArticle: (categoryId, articleId) => api.get(`/help/articles/${categoryId}/${articleId}`),
  search: (q) => api.get('/help/search', { params: { q } }),
};

// Reports API
export const reportsAPI = {
  updateReceiptLineRate: (data) => api.patch('/reports/purchase-receipt-line-rate', data),
  programPnl: (params) => api.get('/reports/program-pnl', { params }),
  programPnlExport: (params, format) => api.get('/reports/program-pnl', {
    params: { ...params, format },
    responseType: 'blob',
  }),
};

export default api;
